import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Button,
  IconButton,
  Collapse,
  Fade,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { getBarberAppointments } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AppointmentList from './AppointmentList';
import AppointmentCalendar from './AppointmentCalendar';
import NewAppointmentCalendar from './NewAppointmentCalendar';
import WorkingHours from './WorkingHours';
import VacationDays from './VacationDays';
import ServiceManagement from './ServiceManagement';
import StoreManagement from './StoreManagement';
import ScheduleManagement from './ScheduleManagement';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ListIcon from '@mui/icons-material/List';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div 
    hidden={value !== index} 
    style={{ marginTop: '20px' }}
  >
    {value === index && children}
  </div>
);

const BarberDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('appointments');
  const [menuOpen, setMenuOpen] = useState(false);
  const [appointmentView, setAppointmentView] = useState('list');
  
  const theme = useTheme();
  const { t } = useLanguage();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery('(max-width:400px)');

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const data = await getBarberAppointments();
      setAppointments(data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAppointmentViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: string | null,
  ) => {
    if (newView !== null) {
      setAppointmentView(newView);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          background: (theme) => theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3 
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {t('barberDashboard.title')}
          </Typography>

          <IconButton 
            sx={{ 
              display: { xs: 'block', sm: 'none' },
              color: 'text.primary',
              transition: 'transform 0.3s ease',
              transform: menuOpen ? 'rotate(180deg)' : 'none',
            }}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Fade in={!menuOpen}>
              <MenuIcon 
                sx={{ 
                  position: 'absolute',
                  transition: 'opacity 0.3s ease',
                  opacity: menuOpen ? 0 : 1,
                }} 
              />
            </Fade>
            <Fade in={menuOpen}>
              <CloseIcon 
                sx={{ 
                  transition: 'opacity 0.3s ease',
                  opacity: menuOpen ? 1 : 0,
                }} 
              />
            </Fade>
          </IconButton>
        </Box>

        <Fade in={menuOpen || window.innerWidth > 600}>
          <Box sx={{ 
            display: { xs: menuOpen ? 'block' : 'none', sm: 'block' },
            mb: 3
          }}>
            <Box sx={{ 
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
            }}>
              {[
                { value: 'appointments', label: t('barberDashboard.appointments') },
                { value: 'services', label: t('barberDashboard.services') },
                { value: 'schedule', label: t('barberDashboard.schedule') },
                { value: 'store', label: t('barberDashboard.store') }
              ].map((tab) => (
                <Button
                  key={tab.value}
                  variant="contained"
                  onClick={() => {
                    setCurrentTab(tab.value);
                    setMenuOpen(false);  // Close menu after selection on mobile
                  }}
                  sx={{
                    backgroundColor: currentTab === tab.value ? 'primary.main' : 'secondary.light',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Fade>

        {currentTab === 'appointments' && (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3
            }}>
              <ToggleButtonGroup
                value={appointmentView}
                exclusive
                onChange={handleAppointmentViewChange}
                aria-label="appointment view"
                sx={{ 
                  bgcolor: theme.palette.action.hover,
                  borderRadius: '24px',
                  p: 0.5,
                  '& .MuiToggleButtonGroup-grouped': {
                    borderRadius: '20px !important',
                    mx: 0.5,
                    border: 'none',
                  }
                }}
                size={isVerySmallScreen ? "small" : "medium"}
              >
                <ToggleButton 
                  value="list" 
                  aria-label="list view" 
                  sx={{ 
                    px: isVerySmallScreen ? 1 : 2,
                    color: appointmentView === 'list' ? theme.palette.primary.main : theme.palette.text.secondary,
                    bgcolor: appointmentView === 'list' ? theme.palette.action.selected : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.action.selected,
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    }
                  }}
                >
                  <ListIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
                </ToggleButton>
                <ToggleButton 
                  value="calendar" 
                  aria-label="original calendar view"
                  sx={{ 
                    px: isVerySmallScreen ? 1 : 2,
                    color: appointmentView === 'calendar' ? theme.palette.primary.main : theme.palette.text.secondary,
                    bgcolor: appointmentView === 'calendar' ? theme.palette.action.selected : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.action.selected,
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    }
                  }}
                >
                  <CalendarTodayIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
                </ToggleButton>
                <ToggleButton 
                  value="newcalendar" 
                  aria-label="new calendar view"
                  sx={{ 
                    px: isVerySmallScreen ? 1 : 2,
                    color: appointmentView === 'newcalendar' ? theme.palette.primary.main : theme.palette.text.secondary,
                    bgcolor: appointmentView === 'newcalendar' ? theme.palette.action.selected : 'transparent',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.action.selected,
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    }
                  }}
                >
                  <CalendarMonthIcon fontSize={isVerySmallScreen ? "small" : "medium"} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {loading ? (
              <Typography sx={{ color: 'text.secondary' }}>
                {t('barberDashboard.loading')}
              </Typography>
            ) : appointmentView === 'list' ? (
              <AppointmentList
                appointments={appointments}
                onStatusUpdate={fetchAppointments}
              />
            ) : appointmentView === 'newcalendar' ? (
              <NewAppointmentCalendar 
                appointments={appointments} 
                onStatusUpdate={fetchAppointments}
              />
            ) : (
              <AppointmentCalendar 
                appointments={appointments} 
                onStatusUpdate={fetchAppointments}
              />
            )}
          </>
        )}

        {currentTab === 'services' && <ServiceManagement />}
        {currentTab === 'store' && <StoreManagement />}
        {currentTab === 'schedule' && <ScheduleManagement />}
      </Paper>
    </Container>
  );
};

export default BarberDashboard; 